import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Monitor, Server, Smartphone, Plus, Pencil, Trash2, Tag, GraduationCap } from "lucide-react";
import {
  getAllVersions,
  createVersion,
  updateVersion,
  deleteVersion,
} from "../../../api/system/appVersions";
import {
  showConfirmDialog,
  showSuccessToast,
  showErrorToast,
} from "../../../components/ConfirmDialog";
import { hasPermission } from "../../../utils/permissionUtils";

const PLATFORMS = [
  {
    key: "pwa_fe",
    label: "PWA FE",
    description: "Frontend Progressive Web App",
    icon: Monitor,
  },
  {
    key: "pwa_be",
    label: "PWA BE",
    description: "Backend API / server",
    icon: Server,
  },
  {
    key: "lesson",
    label: "IMA Lesson",
    description: "Cổng bài học & Giảng dạy",
    icon: GraduationCap,
  },
  {
    key: "mobile",
    label: "Mobile",
    description: "Ứng dụng di động",
    icon: Smartphone,
  },
];

const platformLabel = (key) =>
  PLATFORMS.find((p) => p.key === key)?.label || key;

const emptyForm = {
  version_code: "",
  version_number: "",
  release_notes: "",
};

function bumpPatchSuggestion(code) {
  const m = String(code || "")
    .trim()
    .replace(/^v/i, "")
    .match(/^(\d+)\.(\d+)\.(\d+)$/);
  if (!m) return "";
  return `v${m[1]}.${m[2]}.${Number(m[3]) + 1}`;
}

export default function AppVersionsPage() {
  const canCreate = hasPermission("system.version", "C");
  const canUpdate = hasPermission("system.version", "U");
  const canDelete = hasPermission("system.version", "D");

  const [platform, setPlatform] = useState("pwa_fe");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllVersions();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      showErrorToast(err?.message || "Không thể tải danh sách phiên bản");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredRows = useMemo(
    () => rows.filter((r) => r.platform === platform),
    [rows, platform]
  );

  const latestByPlatform = useMemo(() => {
    const map = { pwa_fe: null, pwa_be: null, lesson: null, mobile: null };
    for (const row of rows) {
      const key = row.platform;
      if (!(key in map)) continue;
      if (!map[key]) {
        map[key] = row;
        continue;
      }
      if (Number(row.version_number) > Number(map[key].version_number)) {
        map[key] = row;
      }
    }
    return map;
  }, [rows]);

  const openCreate = () => {
    const latest = latestByPlatform[platform];
    setEditing(null);
    setForm({
      version_code: bumpPatchSuggestion(latest?.version_code) || "v1.0.0",
      version_number: latest ? String(Number(latest.version_number) + 1) : "1",
      release_notes: "",
    });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      version_code: row.version_code || "",
      version_number: String(row.version_number ?? ""),
      release_notes: row.release_notes || "",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const version_code = form.version_code.trim();
    if (!version_code) {
      showErrorToast("Vui lòng nhập mã phiên bản");
      return;
    }

    const payload = {
      platform,
      version_code,
      release_notes: form.release_notes.trim(),
    };
    if (form.version_number.trim()) {
      payload.version_number = Number(form.version_number);
    }

    setSaving(true);
    try {
      if (editing) {
        await updateVersion(editing.id, payload);
        showSuccessToast("Đã cập nhật phiên bản");
      } else {
        await createVersion(payload);
        showSuccessToast("Đã tạo phiên bản mới");
      }
      setSaving(false);
      closeModal();
      await loadData();
    } catch (err) {
      showErrorToast(err?.message || "Không thể lưu phiên bản");
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const result = await showConfirmDialog({
      title: "Xóa phiên bản?",
      text: `Xóa ${row.version_code} (${platformLabel(row.platform)})?`,
    });
    if (!result.isConfirmed) return;

    try {
      await deleteVersion(row.id);
      showSuccessToast("Đã xóa phiên bản");
      await loadData();
    } catch (err) {
      showErrorToast(err?.message || "Không thể xóa phiên bản");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Phiên bản sử dụng</h1>
          <p className="mt-1 text-sm text-gray-500">
            Theo dõi phiên bản PWA FE, PWA BE, IMA Lesson và Mobile đang dùng.
          </p>
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-700 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-red-800"
          >
            <Plus size={16} />
            Thêm phiên bản {platformLabel(platform)}
          </button>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {PLATFORMS.map((item) => {
          const Icon = item.icon;
          const latest = latestByPlatform[item.key];
          const active = platform === item.key;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setPlatform(item.key)}
              className={`rounded-2xl border p-5 text-left transition ${
                active
                  ? "border-red-200 bg-red-50 shadow-sm"
                  : "border-gray-100 bg-white hover:border-red-100"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div
                    className={`rounded-xl p-2.5 ${
                      active ? "bg-red-700 text-white" : "bg-gray-50 text-gray-500"
                    }`}
                  >
                    <Icon size={18} />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-900">{item.label}</div>
                    <div className="text-xs text-gray-500">{item.description}</div>
                  </div>
                </div>
                <Tag size={16} className={active ? "text-red-600" : "text-gray-300"} />
              </div>
              <div className="mt-4">
                <div className="text-[11px] font-black uppercase tracking-widest text-gray-400">
                  Phiên bản hiện tại
                </div>
                <div className="mt-1 text-2xl font-black text-gray-900">
                  {latest?.version_code || "—"}
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  {latest
                    ? `Build #${latest.version_number} · ${dayjs(latest.created_at).format(
                        "DD/MM/YYYY HH:mm"
                      )}`
                    : "Chưa có phiên bản"}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <div className="text-sm font-bold text-gray-900">
              Lịch sử phiên bản {platformLabel(platform)}
            </div>
            <div className="text-xs text-gray-500">{filteredRows.length} bản ghi</div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead className="border-b border-gray-100 bg-gray-50/50">
              <tr>
                <th className="w-16 p-4 text-center text-[11px] font-black uppercase tracking-widest text-gray-400">
                  STT
                </th>
                <th className="p-4 text-[11px] font-black uppercase tracking-widest text-gray-400">
                  Mã phiên bản
                </th>
                <th className="p-4 text-[11px] font-black uppercase tracking-widest text-gray-400">
                  Build #
                </th>
                <th className="p-4 text-[11px] font-black uppercase tracking-widest text-gray-400">
                  Ghi chú phát hành
                </th>
                <th className="p-4 text-[11px] font-black uppercase tracking-widest text-gray-400">
                  Ngày tạo
                </th>
                <th className="p-4 text-center text-[11px] font-black uppercase tracking-widest text-gray-400">
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan={6} className="p-10 text-center text-sm text-gray-400">
                    Đang tải...
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-10 text-center text-sm text-gray-400">
                    Chưa có phiên bản cho nền tảng này
                  </td>
                </tr>
              ) : (
                filteredRows.map((row, index) => (
                  <tr key={row.id} className="hover:bg-gray-50/60">
                    <td className="p-4 text-center text-xs font-bold text-gray-400">
                      {index + 1}
                    </td>
                    <td className="p-4">
                      <span className="rounded-lg bg-red-50 px-2.5 py-1 text-sm font-bold text-red-700">
                        {row.version_code}
                      </span>
                    </td>
                    <td className="p-4 text-sm font-semibold text-gray-700">
                      #{row.version_number}
                    </td>
                    <td className="max-w-md p-4 text-sm text-gray-600">
                      {row.release_notes || "—"}
                    </td>
                    <td className="p-4 text-sm text-gray-500">
                      {row.created_at
                        ? dayjs(row.created_at).format("DD/MM/YYYY HH:mm")
                        : "—"}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-center gap-2">
                        {canUpdate && (
                          <button
                            type="button"
                            onClick={() => openEdit(row)}
                            className="rounded-lg border border-gray-200 p-2 text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-700"
                            title="Sửa"
                          >
                            <Pencil size={14} />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            onClick={() => handleDelete(row)}
                            className="rounded-lg border border-gray-200 p-2 text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-700"
                            title="Xóa"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                        {!canUpdate && !canDelete && (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
            <div className="border-b border-gray-100 px-6 py-4">
              <h2 className="text-lg font-bold text-gray-900">
                {editing ? "Cập nhật phiên bản" : "Thêm phiên bản"}{" "}
                {platformLabel(platform)}
              </h2>
            </div>
            <form onSubmit={handleSave} className="space-y-4 px-6 py-5">
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500">
                  Mã phiên bản
                </label>
                <input
                  value={form.version_code}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, version_code: e.target.value }))
                  }
                  placeholder="vd: v1.2.3"
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-red-300 focus:ring-2 focus:ring-red-100"
                  required
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500">
                  Build number
                </label>
                <input
                  type="number"
                  min="1"
                  value={form.version_number}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, version_number: e.target.value }))
                  }
                  placeholder="Tự tăng nếu để trống khi tạo mới"
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-red-300 focus:ring-2 focus:ring-red-100"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500">
                  Ghi chú phát hành
                </label>
                <textarea
                  rows={4}
                  value={form.release_notes}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, release_notes: e.target.value }))
                  }
                  placeholder="Mô tả thay đổi của phiên bản..."
                  className="w-full resize-none rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-red-300 focus:ring-2 focus:ring-red-100"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={saving}
                  className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-bold text-gray-600 hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-xl bg-red-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-red-800 disabled:opacity-60"
                >
                  {saving ? "Đang lưu..." : editing ? "Cập nhật" : "Tạo mới"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
